import { formatMotif } from "./format";
import type { FocusCandidate } from "./patterns.api";

const percent = (rate: number) => `${Math.round(rate * 100)}%`;

export const focusName = (focus: FocusCandidate): string =>
  focus.type === "tactical" && focus.key ? formatMotif(focus.key) : focus.title;

/** Why Chessla chose this pattern, from the evidence that started its tracking cycle. */
export const focusWhyCopy = (focus: FocusCandidate, cohort: string): string =>
  focus.type === "tactical"
    ? `Missed in ${Math.round(focus.baselineRate * focus.baselineGames)} of your last ${focus.baselineGames} ${cohort} games.`
    : `${percent(focus.baselineRate)} of your ${cohort} moves in this clock situation were mistakes.`;

/** The exact rule the tracking verdict uses. */
export const focusRuleCopy = (focus: FocusCandidate, cohort: string): string =>
  focus.type === "tactical"
    ? `Improves if it appears in at least ${percent(focus.requiredReduction)} fewer of your next ${focus.windowGames} ${cohort} games.`
    : `Improves if at least ${percent(focus.requiredReduction)} fewer of these moves are mistakes over your next ${focus.windowGames} ${cohort} games.`;

export const focusStatusCopy = (focus: FocusCandidate, cohort: string): string => {
  const change = focus.resultRate == null ? "" : `${percent(focus.baselineRate)} → ${percent(focus.resultRate)}`;
  switch (focus.status) {
    case "accepted":
    case "pending":
      return focus.measuredGames === 0
        ? `Waiting for your next ${cohort} game.`
        : `${focus.measuredGames} of ${focus.windowGames} games measured.`;
    case "inconclusive":
      return "Waiting for eligible games. Short games and games without clock data don't count.";
    case "improved":
      return `Improved: ${change}.`;
    case "regressed":
      return `Not improved yet: ${change}. A new cycle starts automatically.`;
    case "available":
    case "proposed":
    case "dismissed":
      return "Tracking starts automatically once this pattern is proven in your games.";
  }
};

/** A Focus as a coach states it: when to notice the moment, and the question to ask at the board. */
export interface FocusCue {
  when: string;
  ask: string;
}

const CUES: Record<string, FocusCue> = {
  fork: {
    when: "When a knight, queen, or pawn can reach a square attacking two pieces",
    ask: "Can I attack two targets at once, and can they?",
  },
  pin: {
    when: "When an enemy piece stands in front of their king or queen on a line",
    ask: "Can I pin it to what stands behind?",
  },
  skewer: {
    when: "When their king or queen stands in front of another piece on a line",
    ask: "Can I attack the front piece and win the one behind?",
  },
  missed_check: { when: "Before every move", ask: "Which checks do I have, and what does each one win?" },
  missed_capture: { when: "Before every move", ask: "Which of their pieces can I take, and is it really free?" },
  hanging_piece: {
    when: "After every opponent move",
    ask: "Is any of their pieces undefended, and can I win it?",
  },
  discovered_attack: {
    when: "When one of my pieces blocks another piece's line",
    ask: "What does moving it uncover, and can the moving piece attack too?",
  },
  back_rank_threat: {
    when: "When their king has no escape squares on the back rank",
    ask: "Can a rook or queen land on that rank?",
  },
  deflection: {
    when: "When one of their pieces has an important defensive job",
    ask: "Can I force it away from that job?",
  },
  sacrifice: {
    when: "When their king is exposed or my pieces aim at it",
    ask: "Does giving material force something bigger?",
  },
  zwischenzug: {
    when: "Before an obvious recapture or reply",
    ask: "Is there a check or threat I should play first?",
  },
  mating_net: { when: "When their king is short of squares", ask: "Which forcing moves lead to mate?" },
  overloaded_defender: {
    when: "When one of their pieces defends two things",
    ask: "Can I attack one so the other falls?",
  },
  rushed_clock: {
    when: "When checks, captures, or threats are on the board",
    ask: "What is one alternative, and why is my move better?",
  },
  time_pressure: {
    when: "Before your clock runs low",
    ask: "Can I simplify now instead of calculating later?",
  },
};
const FALLBACK_CUE: FocusCue = {
  when: "Before every move",
  ask: "What are the checks, captures, and threats for both sides?",
};

export const focusCue = (key: string): FocusCue => CUES[key] ?? FALLBACK_CUE;

export const focusHabit = (key: string): string => {
  const cue = focusCue(key);
  return `${cue.when}, ask: ${cue.ask}`;
};

/** Habits stick in games with time to think; fast formats get a nudge to practise slower too. */
export const focusPracticeTip = (cohort: string): string | null =>
  cohort === "bullet" || cohort === "blitz"
    ? "Habits stick faster with time to think. Practise this one in rapid or daily games too."
    : null;
